import {
  Autocomplete,
  Button,
  IconButton,
  TextField,
  Typography,
  Grow,
  Fade,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  InputAdornment,
} from "@mui/material";
import { useQuery, useQueryClient } from "react-query";
import { cptSearch, getCategories, getSubDepartment } from "./services";
import { useState } from "react";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import ListAltIcon from "@mui/icons-material/ListAlt";
import {
  SubDepartmentType,
  SubDepartmentSearchType,
  CategoryType,
  CategorySearchType,
  AddPackagePropsType,
  AddValuesType,
} from "./types";
import usePopup from "@hooks/usePopup";
import useAuthentication from "@hooks/useAuthentication";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
function AddPackage({
  departmentId,
  cptGroupId,
  selectedList,
  onAdd,
  loading,
  isCpt,
  setSelectedValue,
  selectedValue,
}: AddPackagePropsType) {
  const { showSnackbar } = usePopup();
  const { user } = useAuthentication();
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useState<SubDepartmentSearchType>({
    deptId: departmentId,
    isActive: 1,
    searchText: "",
  });

  const [cptSearchParams, setCptSearchParams] = useState({
    pageNumber: 0,
    pageSize: 250,
    sortField: "",
    sortOrder: "",
    filter: { searchText: "", status: 2, cptGrpId: cptGroupId },
  });

  const [selectedCpt, setSelectedCpt] = useState<{
    first: number | null;
    second: number | null;
  }>({
    first: null,
    second: null,
  });

  const [editRate, setEditRate] = useState<number | string>(0);

  const [categorySearchParams, setCategorySearchParams] =
    useState<CategorySearchType>({
      deptId: departmentId,
      isActive: 1,
      searchText: "",
    });

  const { data: subDepartments, isLoading: subDepartmentsLoading } = useQuery(
    [`${departmentId}${cptGroupId}addpackagesubdepartment`, searchParams],
    async () =>
      getSubDepartment({ ...searchParams, clinicId: Number(user?.clinicId) }),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: category, isFetching: categoryLoading } = useQuery(
    [
      `${departmentId}${cptGroupId}addpackagecategoriesList`,
      categorySearchParams,
    ],
    async () => getCategories(categorySearchParams),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: cpt, isLoading: cptLoading } = useQuery(
    [`${departmentId}${cptGroupId}packagecpts`, cptSearchParams],
    async () => cptSearch({ ...cptSearchParams,codetype:String(user?.codetype) }),
    { refetchOnWindowFocus: false }
  );

  const handleAddToList = () => {
    if (!selectedValue.subDepartment && !isCpt) {
      showSnackbar({
        message: "Sub department required",
        open: true,
        variant: "warning",
      });
      return;
    }
    if (selectedValue.cpt.length === 0) {
      showSnackbar({
        message: "Please add some CPT",
        open: true,
        variant: "warning",
      });
      return;
    }
    const update = { ...selectedValue };
    update.cpt.forEach((c, i) => {
      update.cpt[i] = {
        ...c,
        packageRate: c?.packageRate || c.cptRate || 0,
        cptRate: c?.cptRate || 0,
      };
    });
    onAdd([...selectedList, selectedValue]);
    setSelectedValue((prev) => ({ ...prev, category: null, cpt: [] }));
  };

  const handleRemove = (index: number) => {
    const updated = selectedList.filter((_, i) => i !== index);
    onAdd(updated);
  };

  const handleRemoveCpt = (parentId: number, childId: number) => {
    const item = selectedList[parentId];
    if (item.cpt.length > 1) {
      item.cpt = item.cpt.filter((_, i) => i !== childId);
    }
    const updated = [...selectedList];
    updated[parentId] = item;
    onAdd(updated);
  };

  const handleSubDepartmentChange = (
    _: any,
    subDepartment: SubDepartmentType | null
  ) => {
    setSelectedValue((prev) => ({ ...prev, subDepartment }));
    setCategorySearchParams((prev) => ({
      ...prev,
      subDeptId: Number(subDepartment?.subDeptId),
    }));
  };

  const handleCategoryChange = (_: any, category: CategoryType | null) => {
    if (category) {
      setSelectedValue((prev) => ({
        ...prev,
        category,
        cpt: category?.cpt || [],
      }));
      queryClient.setQueryData([`${departmentId}cptlistofpackage`], {
        response: category?.cpt || [],
      });
    } else {
      setSelectedValue((prev) => ({ ...prev, category, cpt: [] }));
      setCptSearchParams((prev) => ({
        ...prev,
        filter: { ...prev.filter, searchText: "" },
      }));
    }
  };

  const handleRateEdit = () => {
    const update = [...selectedList]; // clone array
    const parentObj = update[selectedCpt.first as number] as AddValuesType;
    if (!parentObj) return;
    const updatedParent = { ...parentObj };
    const updatedCptArray = [...updatedParent.cpt];
    const updatedCptItem = {
      ...updatedCptArray[selectedCpt.second as number],
      packageRate: Number(editRate),
    };
    updatedCptArray[selectedCpt.second as number] = updatedCptItem;
    updatedParent.cpt = updatedCptArray;
    update[selectedCpt.first as number] = updatedParent;
    onAdd(update);
    setSelectedCpt({ first: null, second: null });
    setEditRate(0);
  };

  const handleCancelEdit = () => {
    setEditRate(0);
    setSelectedCpt({
      first: null,
      second: null,
    });
  };

  return (
    <div className="grid grid-cols-12 pt-2">
      {/* Left input section */}
      <div className="col-span-5 border-2 border-gray-300 rounded-md p-3 flex flex-col items-center justify-center gap-3 bg-white shadow-sm transition-all hover:shadow-md">
        <Autocomplete
          fullWidth
          className={isCpt ? "hidden" : ""}
          value={selectedValue.subDepartment}
          disabled={Boolean(loading)}
          loading={subDepartmentsLoading}
          options={subDepartments?.response || []}
          getOptionLabel={(option) => option.subDeptName}
          isOptionEqualToValue={(option, value) =>
            option.subDeptId === value.subDeptId
          }
          onInputChange={(_, searchText) =>
            setSearchParams((prev) => ({ ...prev, searchText }))
          }
          filterOptions={(option) => option}
          onChange={handleSubDepartmentChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                <span>
                  Sub Department <span className="text-red-500">*</span>
                </span>
              }
            />
          )}
        />

        <Autocomplete
          fullWidth
          className={isCpt ? "hidden" : ""}
          value={selectedValue.category}
          disabled={Boolean(loading)}
          loading={categoryLoading}
          options={category?.response || []}
          getOptionLabel={(option) => option.categoryName}
          isOptionEqualToValue={(option, value) =>
            option.categoryId === value.categoryId
          }
          onChange={handleCategoryChange}
          renderInput={(params) => <TextField {...params} label="Category" />}
        />

        <Autocomplete
          fullWidth
          multiple
          disableCloseOnSelect
          blurOnSelect={false}
          disabled={Boolean(loading)}
          value={selectedValue.cpt}
          options={cpt?.response?.items || []}
          loading={cptLoading}
          getOptionLabel={(option) => `( ${option.cptCode} ) ${option.cptName}`}
          isOptionEqualToValue={(option, value) => option.cptId === value.cptId}
          onChange={(_, cpt) => setSelectedValue((prev) => ({ ...prev, cpt }))}
          onInputChange={(_, searchText) =>
            setCptSearchParams((prev) => ({
              ...prev,
              filter: { ...prev.filter, searchText },
            }))
          }
          renderInput={(params) => <TextField {...params} label="Select CPT" />}
          renderOption={(props, option, { selected }) => (
            <ListItem {...props} disablePadding>
              <ListItemIcon>
                <Checkbox checked={selected} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <div className="grid grid-cols-7">
                    <Typography color={"primary"} className="col-span-2">
                      {option?.cptCode}
                    </Typography>
                    <Typography className="col-span-4">
                      {option.cptName}
                    </Typography>
                  </div>
                }
              />
            </ListItem>
          )}
        />
      </div>

      {/* Middle button */}
      <div className="flex justify-center items-center px-2">
        <Button
          variant="contained"
          color="primary"
          className="rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
          onClick={handleAddToList}
          endIcon={<KeyboardDoubleArrowRightIcon />}
        >
          Add
        </Button>
      </div>

      {/* Right list section */}
      <div className="col-span-6 border-2 border-gray-200 rounded-md p-3 grid grid-cols-1 gap-3 bg-gray-50 transition-all hover:bg-gray-100">
        {selectedList?.length ? (
          selectedList.map((item, first) => (
            <Grow in={true} key={first} timeout={400}>
              <div className="flex flex-col p-2 border border-gray-300 rounded-md bg-white shadow-sm hover:shadow-md transition-all duration-300">
                <div className="border-b pb-1 flex justify-between items-center">
                  <Typography className="text-primary text-sm font-semibold">
                    {item.subDepartment?.subDeptName}
                    {item.category && ` (${item?.category?.categoryName})`}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemove(first)}
                    className="transition-transform hover:scale-110"
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </div>

                <Fade in={true} timeout={500}>
                  <div>
                    {item?.cpt?.map((cpt, second) => {
                      let isSelected = Boolean(
                        selectedCpt.first == first &&
                          selectedCpt.second == second
                      );
                      return (
                        <div
                          key={first + "-" + second}
                          className="grid grid-cols-12 py-1 items-center text-xs text-gray-700"
                        >
                          <div className="col-span-11 flex gap-2 items-center justify-between">
                            <div className="flex gap-2 items-center">
                              <span className="bg-green-500 w-1.5 h-1.5 rounded-full"></span>
                              {cpt.cptName}
                            </div>
                            <TextField
                              size="small"
                              type="number"
                              onFocus={() => {
                                setSelectedCpt({ first, second });
                                setEditRate(
                                  cpt?.packageRate || cpt?.cptRate || 0
                                );
                              }}
                              value={
                                isSelected ? editRate : cpt?.packageRate || 0
                              }
                              onChange={(e) => {
                                setEditRate(e.target.value);
                              }}
                              sx={{ width: 150, paddingLeft: 0 }}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment
                                    position="end"
                                    className="flex gap-2"
                                  >
                                    {isSelected && (
                                      <>
                                        <IconButton
                                          sx={{ padding: 0 }}
                                          color="success"
                                          onClick={handleRateEdit}
                                        >
                                          <CheckIcon
                                            sx={{ fontSize: "1.2rem" }}
                                          />
                                        </IconButton>
                                        <IconButton
                                          sx={{ padding: 0 }}
                                          color="error"
                                          onClick={handleCancelEdit}
                                        >
                                          <CloseIcon
                                            sx={{ fontSize: "1.2rem" }}
                                          />
                                        </IconButton>
                                      </>
                                    )}
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </div>
                          <div className="flex justify-end items-center">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleRemoveCpt(first, second)}
                              className="transition-transform hover:scale-110"
                            >
                              <RemoveCircleIcon fontSize="small" />
                            </IconButton>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Fade>
              </div>
            </Grow>
          ))
        ) : (
          <div className="col-span-full flex justify-center items-center">
            <div className="text-center animate-pulse">
              {Boolean(loading) ? (
                <Typography color="textSecondary">Feching data...</Typography>
              ) : (
                <Typography color="textSecondary">
                  Selected packages will be displayed here
                  <ListAltIcon fontSize="small" />
                </Typography>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AddPackage;
